import { useEffect, useState } from 'react';
import { Alert, Button } from '@mui/material';
import { hasNoticeExpired, setMaintenanceNotice, useMaintenanceNotice } from '@/lib/maintenance';

const POLL_MS = 5000;

/**
 * "ATLAS goes offline in about a minute."
 *
 * Raised by the socket notice the deploy sends a minute before it swaps the
 * containers, and rendered by `Layout` above the routed content so it survives
 * navigation. Receiving the notice also fires `SAVE_NOW_EVENT`, so an open
 * evaluation form has already flushed by the time this appears — the minute is
 * for the forms ATLAS cannot save on someone's behalf.
 *
 * Three states, because "you have a minute" and "it is down" are different
 * things to tell somebody. The switch between them is `/health`: the notice
 * arrives while the old backend is still answering, so the banner waits to see
 * it actually go DOWN before it says the outage has started, and waits for it
 * to answer again before offering the reload.
 *
 * It never reloads the page by itself: a reload during an unsaved manual edit
 * (the admin editors, the bulletin composer) would throw away exactly the work
 * this banner exists to protect.
 */
export function MaintenanceBanner() {
  const notice = useMaintenanceNotice();
  const [isDown, setIsDown] = useState(false);
  const [isBack, setIsBack] = useState(false);

  useEffect(() => {
    if (!notice || isBack) return;
    let cancelled = false;

    const poll = async () => {
      let healthy = false;
      try {
        const res = await fetch('/health', { cache: 'no-store' });
        healthy = res.ok;
      } catch {
        healthy = false;
      }
      if (cancelled) return;
      if (!healthy) setIsDown(true);
      else if (isDown) setIsBack(true);
      // Still up long after the restart was due: the deploy aborted, or this was
      // a test notice. Withdraw it rather than leave a warning standing that is
      // no longer true.
      else if (hasNoticeExpired(notice)) setMaintenanceNotice(null);
    };

    const timer = setInterval(() => void poll(), POLL_MS);
    return () => {
      cancelled = true;
      clearInterval(timer);
    };
  }, [notice, isDown, isBack]);

  if (!notice) return null;

  if (isBack) {
    return (
      <Alert
        severity="success"
        sx={{ mb: 2 }}
        action={
          <Button color="inherit" size="small" onClick={() => window.location.reload()}>
            Reload
          </Button>
        }
      >
        ATLAS has been updated. Reload to pick up the new version.
      </Alert>
    );
  }

  if (isDown) {
    return (
      <Alert severity="warning" sx={{ mb: 2 }}>
        <strong>ATLAS is updating.</strong> It should be back in about{' '}
        {notice.etaMinutes} minutes. This page will say so when it is.
      </Alert>
    );
  }

  const lead =
    notice.leadSeconds >= 90 ? `${Math.round(notice.leadSeconds / 60)} minutes` : 'a minute';

  return (
    <Alert severity="warning" sx={{ mb: 2 }}>
      <strong>ATLAS goes offline for an update in about {lead}</strong>, and will
      be away for around {notice.etaMinutes} minutes. Evaluation forms have just
      saved themselves — if you are part-way through anything else, save it now.
    </Alert>
  );
}

export default MaintenanceBanner;
